import { useEffect, useState, useCallback } from "react";

const HANDLE_SIZE = 9;
const ROTATE_HANDLE_OFFSET = 24;

/**
 * Overlay handle resize (4 sudut) + rotate (handle di atas), digambar sebagai
 * div HTML biasa di atas canvas (bukan bagian dari SVG), diposisikan berdasarkan
 * getBoundingClientRect() elemen yang lagi dipilih.
 *
 * Catatan desain: box seleksi ini axis-aligned (gak ikut rotasi objek), sama kayak
 * behavior di gambar referensi. Ini simplifikasi yang disengaja — cukup buat resize
 * & rotate yang predictable, tanpa perlu geometri rotated-box yang lebih rumit.
 */
export default function SelectionOverlay({
  svgEl,
  wrapperEl,
  selectedId,
  transform,
  onScaleChange,
  onRotateChange,
  onMove,
  onDragStart,
  onDragEnd,
}) {
  const [box, setBox] = useState(null);

  const measure = useCallback(() => {
    if (!svgEl || !wrapperEl || !selectedId) {
      setBox(null);
      return;
    }
    const target = svgEl.querySelector(`#${CSS.escape(selectedId)}`);
    if (!target) {
      setBox(null);
      return;
    }
    const targetRect = target.getBoundingClientRect();
    const wrapperRect = wrapperEl.getBoundingClientRect();
    setBox({
      left: targetRect.left - wrapperRect.left + wrapperEl.scrollLeft,
      top: targetRect.top - wrapperRect.top + wrapperEl.scrollTop,
      width: targetRect.width,
      height: targetRect.height,
      screenLeft: targetRect.left,
      screenTop: targetRect.top,
      screenWidth: targetRect.width,
      screenHeight: targetRect.height,
    });
  }, [svgEl, wrapperEl, selectedId]);

  useEffect(() => {
    measure();
    // re-measure tiap transform selected node berubah (hasil edit manual atau drag)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measure, transform.x, transform.y, transform.rotate, transform.scaleX, transform.scaleY, transform.skewX, transform.skewY]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!box || !selectedId) return null;

  const cxScreen = box.screenLeft + box.screenWidth / 2;
  const cyScreen = box.screenTop + box.screenHeight / 2;

  const corners = [
    { key: "nw", left: box.left, top: box.top, sx: -1, sy: -1, cursor: "nwse-resize" },
    { key: "ne", left: box.left + box.width, top: box.top, sx: 1, sy: -1, cursor: "nesw-resize" },
    { key: "sw", left: box.left, top: box.top + box.height, sx: -1, sy: 1, cursor: "nesw-resize" },
    { key: "se", left: box.left + box.width, top: box.top + box.height, sx: 1, sy: 1, cursor: "nwse-resize" },
  ];

  const handleResizeStart = (corner) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStart?.();
    const startX = e.clientX;
    const startY = e.clientY;
    const startScaleX = transform.scaleX;
    const startScaleY = transform.scaleY;
    const halfWidth = Math.max(box.screenWidth / 2, 1);
    const halfHeight = Math.max(box.screenHeight / 2, 1);

    const onMoveDrag = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      const nextScaleX = Math.max(0.05, startScaleX + (corner.sx * dx * startScaleX) / halfWidth);
      const nextScaleY = Math.max(0.05, startScaleY + (corner.sy * dy * startScaleY) / halfHeight);
      // hold Shift buat lock aspect ratio (samain scale X & Y)
      if (moveEvent.shiftKey) {
        const uniform = Math.max(nextScaleX, nextScaleY);
        onScaleChange(selectedId, uniform, uniform);
      } else {
        onScaleChange(selectedId, nextScaleX, nextScaleY);
      }
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMoveDrag);
      window.removeEventListener("mouseup", onUp);
      onDragEnd?.();
    };
    window.addEventListener("mousemove", onMoveDrag);
    window.addEventListener("mouseup", onUp);
  };

  const handleRotateStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStart?.();
    const startAngle = Math.atan2(e.clientY - cyScreen, e.clientX - cxScreen) * (180 / Math.PI);
    const startRotate = transform.rotate;

    const onMoveDrag = (moveEvent) => {
      const angle = Math.atan2(moveEvent.clientY - cyScreen, moveEvent.clientX - cxScreen) * (180 / Math.PI);
      let delta = angle - startAngle;
      let next = startRotate + delta;
      if (moveEvent.shiftKey) next = Math.round(next / 15) * 15; // snap tiap 15deg
      onRotateChange(selectedId, next);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMoveDrag);
      window.removeEventListener("mouseup", onUp);
      onDragEnd?.();
    };
    window.addEventListener("mousemove", onMoveDrag);
    window.addEventListener("mouseup", onUp);
  };

  // drag di AREA KOTAK SELEKSI (bukan cuma handle) buat mindahin posisi. Ini yang bikin
  // grup (<g>) bisa dipindah — grup gak punya "badan" sendiri yang bisa diklik di canvas
  // (klik selalu kena child paling dalam), jadi drag-to-move mesti lewat overlay ini.
  const handleMoveStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragStart?.();
    const startX = e.clientX;
    const startY = e.clientY;
    const startTx = transform.x;
    const startTy = transform.y;
    let dragging = false;
    const DRAG_THRESHOLD = 2;

    const onMoveDrag = (moveEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      if (!dragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
        dragging = true;
      }
      if (dragging) onMove(selectedId, startTx + dx, startTy + dy);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMoveDrag);
      window.removeEventListener("mouseup", onUp);
      onDragEnd?.();
    };
    window.addEventListener("mousemove", onMoveDrag);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 30 }}>
      <div
        onMouseDown={onMove ? handleMoveStart : undefined}
        title={onMove ? "Drag buat pindah posisi" : undefined}
        className="absolute border border-[#2F8F6F] pointer-events-auto"
        style={{
          left: box.left,
          top: box.top,
          width: box.width,
          height: box.height,
          cursor: onMove ? "move" : "default",
          background: "transparent",
        }}
      />

      {/* garis penghubung ke rotate handle */}
      <div
        className="absolute w-px bg-[#2F8F6F]/60"
        style={{ left: box.left + box.width / 2, top: box.top - ROTATE_HANDLE_OFFSET, height: ROTATE_HANDLE_OFFSET }}
      />
      <div
        onMouseDown={handleRotateStart}
        title="Drag buat rotate (tahan Shift buat snap 15°)"
        className="absolute w-3.5 h-3.5 bg-[#2F8F6F] rounded-full border-2 border-[#0F0F0F] cursor-grab active:cursor-grabbing pointer-events-auto hover:scale-125 transition-transform"
        style={{
          left: box.left + box.width / 2 - 7,
          top: box.top - ROTATE_HANDLE_OFFSET - 7,
        }}
      />

      {corners.map((c) => (
        <div
          key={c.key}
          onMouseDown={handleResizeStart(c)}
          title="Drag buat resize (tahan Shift buat lock rasio)"
          className="absolute bg-[#2F8F6F] border-2 border-[#0F0F0F] pointer-events-auto hover:scale-125 transition-transform"
          style={{
            left: c.left - HANDLE_SIZE / 2,
            top: c.top - HANDLE_SIZE / 2,
            width: HANDLE_SIZE,
            height: HANDLE_SIZE,
            cursor: c.cursor,
          }}
        />
      ))}
    </div>
  );
}
